/**
 * Error-text helpers for the RBAC / partner admin surfaces.
 *
 * The axios interceptor (config/axiosConfig.js) rethrows every HTTP failure as an
 * Error carrying `.status` and `.data` (the raw body) — `error.response` is NOT
 * kept, so never read `err.response.data` here. The backend uses three body shapes
 * (Partner tier guide §0), so read `error` first, then `detail`:
 *   - business / permission / not-found:  { error: "<message>", status_code, ... }
 *   - a few controller checks:            { detail: "<message>" }
 *   - request validation (422):           { detail: [{ loc, msg, type }, ...] }
 */

/**
 * Flatten a FastAPI 422 `detail` list into one sentence naming the fields —
 * returning the raw array would render an object as a React child and throw.
 */
export const describeValidationDetail = (detail) => {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((entry) => {
        if (typeof entry === 'string') return entry;
        const message = entry?.msg || entry?.message;
        if (!message) return null;
        const field = Array.isArray(entry?.loc) ? entry.loc[entry.loc.length - 1] : null;
        return field ? `${field}: ${message}` : message;
      })
      .filter(Boolean);
    return parts.length ? parts.join('. ') : null;
  }
  if (detail && typeof detail === 'object') return detail.msg || detail.message || null;
  return null;
};

/** The backend's own message for a failed call, or '' when there is none. */
export const apiErrorText = (err) =>
  (
    err?.data?.error ||
    describeValidationDetail(err?.data?.detail) ||
    err?.data?.message ||
    err?.message ||
    ''
  ).toString();

/** User-facing text for any failed API call. */
export const describeApiError = (err, fallback = 'Something went wrong.') => {
  if (!err) return fallback;
  if (err.isTimeout) {
    return 'This is taking longer than expected and may still be running on the server. Refresh in a moment to see the result.';
  }
  if (err.isNetworkError) return "Couldn't reach the server. Check your connection and try again.";
  return apiErrorText(err) || fallback;
};

/**
 * Map known backend messages to friendlier copy (the guide's error tables).
 * Match on status (when given) plus a pattern over the backend text; first match
 * wins. Anything unmatched falls back to the backend's own message.
 *
 * @param {Error} err
 * @param {Array<{ status?: number, match: RegExp, message: string | ((m: RegExpMatchArray) => string) }>} rules
 * @param {string} [fallback]
 */
export const mapApiError = (err, rules = [], fallback) => {
  const raw = apiErrorText(err);
  for (const rule of rules) {
    if (rule.status && rule.status !== err?.status) continue;
    const m = raw.match(rule.match);
    if (m) return typeof rule.message === 'function' ? rule.message(m) : rule.message;
  }
  return describeApiError(err, fallback);
};

export const isNotFound = (err) => err?.status === 404;
